export const EV_TYPES = {
  FILE: "file",
  IMAGE: "image",
  // VIDEO: "video",   // video evidence disabled for now — no longer saved/served
};

export const MAX_EVIDENCE_PER_TYPE = { image: 2, file: 2 /*, video: 1 */ };
// Witnesses get a tighter per-turn count cap than the filer/disputant.
export const WITNESS_MAX_EVIDENCE_PER_TYPE = { image: 1, file: 1 };

// Per-turn evidence count caps, by the turn author's role.
export function evidenceLimitsFor(role) {
  return role === "witness" ? WITNESS_MAX_EVIDENCE_PER_TYPE : MAX_EVIDENCE_PER_TYPE;
}

export const MAX_FILE_SIZE_BYTES = {
  image: 3 * 1024 * 1024,
  file: 3 * 1024 * 1024,
};

// Page cap for PDF documents. Page count (not byte size) is what drives AI
// token cost and hits provider page limits — Anthropic caps PDFs at 100 pages
// on 200k-context models, and each page is billed as text + an image. 40 keeps
// a multi-PDF, multi-turn dispute comfortably within every provider's budget.
export const MAX_PDF_PAGES = 40;

export const EV_TYPE_LABEL = { image: "images", file: "files" /*, video: "URLs" */ };

export function countEvidenceByType(items) {
  const counts = { image: 0, file: 0 /*, video: 0 */ };
  for (const item of items) {
    // Video evidence disabled for now — only file-backed rows have content.
    const hasContent = /* item.type === EV_TYPES.VIDEO ? !!item.url : */ !!item.file;
    if (hasContent && counts[item.type] !== undefined) counts[item.type] += 1;
  }
  return counts;
}

export function makeEmptyEvidenceRow() {
  return { id: Date.now() + Math.random(), type: EV_TYPES.IMAGE, file: null, url: "", info: "" };
}

// Validate the evidence array right before sending to the backend.
// Returns null if OK, or a user-facing message describing the overflow.
export function validateEvidenceCap(validItems, maxPerType = MAX_EVIDENCE_PER_TYPE) {
  const counts = countEvidenceByType(validItems);
  const over = Object.entries(counts).filter(([t, n]) => n > (maxPerType[t] ?? 0));
  if (over.length === 0) return null;
  const parts = over.map(([t, n]) => `${n} ${EV_TYPE_LABEL[t]}`);
  const limits = `${maxPerType.image} image(s), ${maxPerType.file} file(s)`;
  return `Evidence limit: max ${limits} per turn (got ${parts.join(", ")}).`;
}

// Returns null if all file sizes are within limits, or a user-facing error message.
export function validateFileSizes(items) {
  for (const item of items) {
    if (/* item.type === EV_TYPES.VIDEO || */ !item.file) continue;
    const limit = MAX_FILE_SIZE_BYTES[item.type];
    if (limit && item.file.size > limit) {
      const mb = Math.round(limit / 1024 / 1024);
      return `"${item.file.name}" exceeds the ${mb} MB limit for ${EV_TYPE_LABEL[item.type]}.`;
    }
  }
  return null;
}

export function filterValidEvidence(items) {
  // Video evidence disabled for now — only file-backed rows are valid.
  return items.filter((e) => !!e.file);
}

// Every uploaded item must explain how it relates to the dispute. Call after
// filterValidEvidence(). Returns null if OK, or a user-facing message.
export function validateEvidenceInfoRequired(validItems) {
  const missing = validItems.some((e) => !e.info || !e.info.trim());
  if (missing) {
    return "Please explain how each piece of evidence is related to the dispute.";
  }
  return null;
}

// Video evidence disabled for now.
// // Platforms allowed for video evidence URLs. These have their own moderation
// // and length limits so we don't need to download the video to enforce a cap.
// export const ALLOWED_VIDEO_DOMAINS = [
//   "youtube.com", "www.youtube.com", "m.youtube.com", "youtu.be",
//   "vimeo.com", "www.vimeo.com", "player.vimeo.com",
//   "loom.com", "www.loom.com",
//   "drive.google.com",
//   "dropbox.com", "www.dropbox.com",
//   "streamable.com", "www.streamable.com",
// ];
//
// // Returns null if the URL is valid and on an allowed platform, or an error string.
// export function validateVideoUrl(url) {
//   if (!url) return null;
//   try {
//     const { hostname } = new URL(url);
//     if (!ALLOWED_VIDEO_DOMAINS.includes(hostname)) {
//       return "Video must be from YouTube, Vimeo, Loom, Google Drive, or Dropbox.";
//     }
//   } catch {
//     return "Please enter a valid URL (e.g. https://youtube.com/...).";
//   }
//   return null;
// }
